import moment from 'moment';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as signalR from '@microsoft/signalr';
import HelperMixin from '../../Shared/HelperMixin.vue';

// SOS-12 — Operations console for §4 SOS. Two modes:
//   "active": uses GET api/admin/Sos/Active (statuses 1+2 only)
//   "history": uses GET api/admin/Sos/History with status/date filters
// Each entry is plotted on a Leaflet map; the operator can click on the
// list to focus the map and trigger Resolve (status 3) or False Alarm
// (status 4) from the row.
//
// H13 (audit 2026-06-02): the canonical SosEvent.Status map is
//   1 = Active, 2 = Dispatched, 3 = Resolved, 4 = FalseAlarm, 9 = Cancelled.
// This console previously used 5=Resolved / 6=FalseAlarm, which NEVER
// matched the DB — so colors/labels/stats were wrong and QuickResolve
// posted an out-of-range NewStatus. All status handling below now uses 3/4.
//
// C5 (audit 2026-06-02): the console now opens a SignalR connection to the
// /sos-hub `security_center` group so realtime SosTriggered / SosResolved /
// SosFalseAlarm / SosCancelled / SosCallbackRequested pushes refresh the
// list live (mirrors the LiveTracking hub pattern). A 10s setInterval is a
// backstop in case a push is missed.

const STATUS_COLORS = {
    1: '#ef4444', // Active
    2: '#f59e0b', // Dispatched
    3: '#22c55e', // Resolved
    4: '#94a3b8', // False alarm
};

// Hub URL — mirrors LiveTracking.js env-override pattern; the hub is mapped
// at /sos-hub in both Management/Startup.cs and MobileApp/Startup.cs.
const SOS_HUB_URL = (typeof process !== 'undefined' && process.env && process.env.VUE_APP_SOS_HUB_URL)
    || '/sos-hub';

export default {
    name: 'Sos',
    mixins: [HelperMixin],

    async created() {
        // Imperative, non-reactive refs (Vue 2 strips `_`-prefixed data() keys,
        // see LiveTracking.js note). Held on the instance directly.
        this._hubConn = null;
        this._pollTimer = null;

        this.loginDetails = await this.CheckLoginStatus();
        if (!this.requireUserType([1])) return;
        // First manual load shows the (non-blocking) loading state.
        await this.LoadEvents(true);

        // C5: open the realtime SOS feed + a polling backstop. The interval
        // fires LoadEvents() with no arg (showLoading=false) so the 10s poll
        // refreshes the list silently — no $blockUI blackout (H-ux).
        this.ConnectHub();
        this._pollTimer = setInterval(() => this.LoadEvents(), 10000);
    },

    mounted() {
        this.$nextTick(() => this.InitMap());
    },

    beforeDestroy() {
        // C5: tear down the realtime feed + polling backstop before the map.
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
        this.DisconnectHub();
        if (this._map) {
            try { this._map.remove(); } catch (e) { /* noop */ }
            this._map = null;
        }
    },

    data() {
        return {
            Events: [],
            // H-ux (audit 2026-06-03): local loading flag replaces the global
            // $blockUI full-screen blackout. Mirrors LiveTracking.js `loading`.
            // Only the FIRST manual load shows a blocking state; the 10s polling
            // backstop and every SignalR push refresh silently (loading stays
            // false) so the console never blacks out on interval/push.
            loading: true,
            Stats: { active: 0, pending: 0, dispatched: 0, resolvedToday: 0 },
            Mode: 'active',
            StatusFilter: '',
            From: '',
            To: '',
            SelectedId: null,
            _map: null,
            _markers: [],
        };
    },

    methods: {
        StatusLabel(s) {
            if (s === 1) return 'مُشغَّلة';
            if (s === 2) return 'مُسندة';
            if (s === 3) return 'محلولة';
            if (s === 4) return 'كاذبة';
            if (s === 9) return 'مُلغاة';
            return '—';
        },
        FormatTime(t) {
            if (!t) return '—';
            return moment(t).format('DD/MM HH:mm');
        },
        Refresh() {
            // Manual refresh button → show the (non-blocking) loading state.
            this.LoadEvents(true);
        },
        InitMap() {
            if (this._map) return;
            const el = this.$refs.mapRoot;
            if (!el) return;
            this._map = L.map(el, { zoomControl: true }).setView([32.8872, 13.1913], 6); // Tripoli centre
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '© OpenStreetMap'
            }).addTo(this._map);
            this.PaintMarkers();
        },
        PaintMarkers() {
            if (!this._map) return;
            // `_markers` is a `_`-prefixed key, which Vue 2 strips from data()
            // (see created() note) so it can be `undefined` on first paint —
            // guard the forEach (and Events) so the map initializes cleanly
            // with zero markers and no console error.
            (this._markers || []).forEach(m => this._map.removeLayer(m));
            this._markers = [];
            const bounds = [];
            (this.Events || []).forEach(ev => {
                if (!ev.latitude || !ev.longitude) return;
                const color = STATUS_COLORS[ev.status] || '#6b7280';
                const html = '<div style="width:18px;height:18px;border-radius:50%;background:' + color +
                    ';border:3px solid #fff;box-shadow:0 0 8px ' + color + '"></div>';
                const icon = L.divIcon({ html, className: 'sos-marker', iconSize: [18, 18], iconAnchor: [9, 9] });
                const m = L.marker([ev.latitude, ev.longitude], { icon }).addTo(this._map);
                m.bindPopup(
                    '<strong>' + (ev.user && ev.user.name || ('#' + (ev.user && ev.user.id))) + '</strong><br/>' +
                    (ev.user && ev.user.phone || '') + '<br/>' +
                    this.FormatTime(ev.triggeredAt)
                );
                m.on('click', () => { this.SelectedId = ev.id; });
                this._markers.push(m);
                bounds.push([ev.latitude, ev.longitude]);
            });
            if (bounds.length) {
                try { this._map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 }); } catch (e) { /* noop */ }
            }
        },
        SelectEvent(ev) {
            this.SelectedId = ev.id;
            if (this._map && ev.latitude && ev.longitude) {
                this._map.setView([ev.latitude, ev.longitude], 14);
            }
        },
        ComputeStats(items) {
            const now = moment().startOf('day');
            const stats = { active: 0, pending: 0, dispatched: 0, resolvedToday: 0 };
            items.forEach(it => {
                if (it.status === 1) stats.pending++;
                if (it.status === 2) stats.dispatched++;
                if (it.status === 1 || it.status === 2) stats.active++;
                if (it.status === 3 && it.resolvedAt && moment(it.resolvedAt).isSameOrAfter(now)) {
                    stats.resolvedToday++;
                }
            });
            this.Stats = stats;
        },
        async LoadEvents(showLoading = false) {
            // H-ux (audit 2026-06-03): NO $blockUI here. A full-screen blackout
            // on every 10s poll AND every SignalR push made the operations
            // console unusable (it blacked out ~every 10s and on each event).
            // Use a local non-blocking `loading` flag instead, and only show it
            // when explicitly requested (the first manual load). Interval/push
            // refreshes call LoadEvents() with no arg → showLoading=false → the
            // list updates silently with no visual interruption.
            if (showLoading) this.loading = true;
            try {
                if (this.Mode === 'active') {
                    const res = await this.$http.GetSosActive();
                    this.Events = res.data || [];
                } else {
                    const from = this.From ? this.ChangeDate(this.From) : '';
                    const to = this.To ? this.ChangeDate(this.To) : '';
                    const res = await this.$http.GetSosHistory(1, 50, this.StatusFilter, from, to);
                    this.Events = (res.data && res.data.items) || [];
                }
                this.ComputeStats(this.Events);
                this.$nextTick(() => this.PaintMarkers());
            } catch (e) {
                this.Events = [];
                this.ComputeStats([]);
            } finally {
                if (showLoading) this.loading = false;
            }
        },
        async QuickResolve(ev, status) {
            // status: 3 = Resolved ("حل"), 4 = FalseAlarm ("إنذار كاذب").
            // Posts the canonical SosEvent.Status (H13) as NewStatus.
            try {
                await this.$http.ResolveSos(ev.id, {
                    NewStatus: status,
                    ResolutionNote: status === 4 ? 'إنذار كاذب — تم التحقق' : 'تم الحل من اللوحة'
                });
                this.LoadEvents();
            } catch (e) {
                // H-ux (audit 2026-06-03): $helper.FailMessage does not exist
                // (Shared/Helper.js exposes only ShowMessage), so the old branch
                // was dead and always fell through to a raw browser alert(). Use
                // the canonical Swal toast the rest of the dashboard uses.
                const msg = e?.response?.data?.Message || 'تعذّر تحديث حالة البلاغ';
                this.$helper.ShowMessage('error', 'خطأ', msg);
            }
        },

        // ============ Realtime SOS hub (C5) ============

        async ConnectHub() {
            if (this._hubConn) return;

            // Management dashboard runs under Cookie auth (see Management/Startup.cs);
            // withCredentials sends the auth cookie on the negotiate POST — which is
            // what SosHub validates ([Authorize] + UserType claim). Mirrors the
            // LiveTracking hub wiring.
            const conn = new signalR.HubConnectionBuilder()
                .withUrl(SOS_HUB_URL, { withCredentials: true })
                .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
                .withServerTimeout(60000)
                .withKeepAliveInterval(15000)
                .build();

            // Any SOS lifecycle push refreshes the list from the source of truth.
            // A new SosTriggered is prepended optimistically so the operator sees
            // it instantly, then LoadEvents reconciles (fills user/category, etc.).
            const refresh = () => { this.LoadEvents(); };
            conn.on('SosTriggered', (payload) => {
                this.PrependTriggered(payload);
                refresh();
            });
            conn.on('SosResolved', refresh);
            conn.on('SosFalseAlarm', refresh);
            conn.on('SosCancelled', refresh);
            conn.on('SosCallbackRequested', refresh);

            conn.onreconnected(() => { this.JoinSecurityCenter(); });

            try {
                await conn.start();
                this._hubConn = conn;
                await this.JoinSecurityCenter();
            } catch (err) {
                // The 10s polling backstop covers us if the hub is unreachable;
                // automatic-reconnect keeps retrying on its back-off schedule.
                console.error('Sos: hub start failed:', err && err.message);
            }
        },

        async JoinSecurityCenter() {
            if (!this._hubConn) return;
            try {
                await this._hubConn.invoke('JoinSecurityCenter');
            } catch (err) {
                console.error('Sos: JoinSecurityCenter failed:', err && err.message);
            }
        },

        async DisconnectHub() {
            if (!this._hubConn) return;
            const conn = this._hubConn;
            this._hubConn = null;
            try { await conn.stop(); } catch (e) { /* ignore */ }
        },

        PrependTriggered(payload) {
            // Optimistic insert in "active" mode only — the backstop LoadEvents
            // immediately replaces this with the fully-shaped server row. Map the
            // SosTriggered wire shape (PascalCase, flat user fields) to the list
            // item shape returned by GET Active.
            if (this.Mode !== 'active' || !payload) return;
            const id = payload.Id ?? payload.id ?? payload.SosEventId ?? payload.sosEventId;
            if (id == null) return;
            if (this.Events.some(e => e.id === id)) return;
            this.Events.unshift({
                id,
                status: 1,
                latitude: payload.Latitude ?? payload.latitude ?? null,
                longitude: payload.Longitude ?? payload.longitude ?? null,
                address: payload.Address ?? payload.address ?? null,
                triggeredAt: payload.TriggeredAt ?? payload.triggeredAt ?? new Date().toISOString(),
                user: {
                    id: payload.UserId ?? payload.userId ?? null,
                    name: payload.Name ?? payload.name ?? null,
                    phone: payload.Phone ?? payload.phone ?? null
                }
            });
            this.ComputeStats(this.Events);
        }
    }
};
